import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { TfiLocationPin } from "react-icons/tfi";
import ApiService from "../nhrc_apis/ApiService";

function UstTotalApplications() {
    const [page, setPage] = useState(1);
    const pageSize = 4;
    const navigate = useNavigate();
    const location = useLocation();

    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchInput, setSearchInput] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [registrationCounts, setRegistrationCounts] = useState({});

    const fetchRegistrationCounts = useCallback(async (trainingList) => {
        const counts = {};
        await Promise.allSettled(
            trainingList.map(async (training) => {
                try {
                    const res = await ApiService.TrainingAPI.getTrainingRegistrations(training.id);
                    const data = res?.data;
                    const list = Array.isArray(data)
                        ? data
                        : Array.isArray(data?.data)
                        ? data.data
                        : Array.isArray(data?.registrations)
                        ? data.registrations
                        : [];

                    const students     = list.filter((r) => r.member_type === "STUDENT").length;
                    const employees    = list.filter((r) => r.member_type === "HR" || r.member_type === "EMPLOYEE").length;
                    const total        = list.length;

                    counts[training.id] = { total, students, employees };
                } catch {
                    counts[training.id] = { total: 0, students: 0, employees: 0 };
                }
            })
        );
        setRegistrationCounts(counts);
    }, []);

    const fetchTrainings = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            console.log("━━━━ Fetching all training programs ━━━━");

            if (!ApiService || !ApiService.TrainingAPI || !ApiService.TrainingAPI.getAllTrainings) {
                setError("ApiService not loaded. Check your import path.");
                setLoading(false);
                return;
            }

            const response = await ApiService.TrainingAPI.getAllTrainings();

            console.log("GET ALL TRAININGS FULL RESPONSE :", response);
            console.log("GET ALL TRAININGS DATA          :", response?.data);

            const data = response?.data;

            const list = Array.isArray(data)
                ? data
                : Array.isArray(data?.data)
                ? data.data
                : Array.isArray(data?.results)
                ? data.results
                : Array.isArray(data?.trainings)
                ? data.trainings
                : [];

            console.log("PARSED TRAINING LIST :", list);

            const mapped = list.map((item) => ({
                id:               item.id || item._id || item.training_id,
                title:            item.title || item.program_title || "Untitled",
                location:         item.location || "—",
                organizername:    item.trainer_name || item.organizer_name || "—",
                programcategory:  item.program_category || "—",
                speakername:      item.trainer_name || "—",
                eventmode:        item.training_mode || "—",
                status:           item.status || "Open",
                eventdate:        item.start_date || "—",
                time:             item.start_time && item.end_time
                                    ? `${item.start_time} - ${item.end_time}`
                                    : "—",
                students:         item.students        || item.student_count        || 0,
                employees:        item.employees       || item.employee_count       || 0,
                representatives:  item.representatives || item.representative_count || 0,
                bannerImage:      item.banner_image      || "",
                shortDescription: item.short_description || "",
                capacity:         item.capacity          || 0,
                contactEmail:     item.contact_email     || "",
                endDate:          item.end_date          || "",
            }));

            console.log("MAPPED JOBS :", mapped);
            setJobs(mapped);

            // ✅ Fetch registration counts for all trainings
            await fetchRegistrationCounts(mapped);

        } catch (err) {
            console.error("━━━━ FETCH ERROR ━━━━");
            console.error("ERROR        :", err);
            console.error("ERROR MESSAGE:", err?.message);
            console.error("ERROR STATUS :", err?.response?.status);
            console.error("ERROR DATA   :", err?.response?.data);
            setError("Failed to load training programs. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [fetchRegistrationCounts]);

    useEffect(() => {
        fetchTrainings();
    }, [fetchTrainings, location.key]);

    const filteredJobs = useMemo(() => {
        return jobs.filter(
            (job) =>
                job.title.toLowerCase().includes(searchInput.toLowerCase()) ||
                job.location.toLowerCase().includes(searchInput.toLowerCase())
        );
    }, [jobs, searchInput]);

    const totalPages = Math.ceil(filteredJobs.length / pageSize);

    const currentJobs = useMemo(() => {
        const startIndex = (page - 1) * pageSize;
        return filteredJobs.slice(startIndex, startIndex + pageSize);
    }, [filteredJobs, page, pageSize]);

    const allSelected =
        currentJobs.length > 0 &&
        currentJobs.every((job) => selectedIds.includes(job.id));

    const handleToggleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(currentJobs.map((job) => job.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleToggleSelect = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleSearch = () => {
        setPage(1);
    };

    const handleDelete = () => {
        if (selectedIds.length === 0) {
            alert("Please select item(s) to delete");
            return;
        }
        if (!window.confirm(`Delete ${selectedIds.length} training(s)?`)) return;
        setJobs((prevJobs) =>
            prevJobs.filter((job) => !selectedIds.includes(job.id))
        );
        setSelectedIds([]);
        setPage(1);
    };

    const handlePreview = (job) => {
        navigate("/dashboard/UpdateSkills", {
            state: { job, returnPath: "/dashboard/UpdateSkills" },
        });
    };

    const handleViewResponses = (job) => {
        navigate(`/dashboard/UstTotalApplicationsRes/${job.id}`, {
            state: {
                jobTitle: job.title,
                jobId: job.id,
                returnPath: `/dashboard/UstTotalApplicationsRes/${job.id}`,
            },
        });
    };

    const handleAddJob = () => {
        navigate("/dashboard/UpdateSkills", {
            state: { returnPath: "/dashboard/UpdateSkills" },
        });
    };

    if (loading) {
        return (
            <section className="Hrjobpost">
                <h3 className="training-title mt-1 fw-bold" style={{ color: "#8D0000" }}>
                    UPDATE SKILLS & TRAINING
                </h3>
                <div className="container mt-5 text-center py-5">
                    <div className="spinner-border text-danger" role="status" />
                    <p className="mt-3 text-muted">Loading training programs...</p>
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="Hrjobpost">
                <h3 className="training-title mt-1 fw-bold" style={{ color: "#8D0000" }}>
                    UPDATE SKILLS & TRAINING
                </h3>
                <div className="container mt-5 text-center py-5">
                    <p className="text-danger fw-semibold">{error}</p>
                    <button
                        className="btn btn-outline-danger mt-2"
                        onClick={fetchTrainings}
                    >
                        Retry
                    </button>
                </div>
            </section>
        );
    }

    return (
        <section className="Hrjobpost">
            <h3 className="training-title mt-1 fw-bold" style={{ color: "#8D0000" }}>
                UPDATE SKILLS & TRAINING
            </h3>
            <div className="container mt-5">

                {/* HEADER */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h5 className="mb-0">All Events</h5>
                        <small className="text-muted">
                            Total: {filteredJobs.length} of {jobs.length}
                        </small>
                    </div>
                    <button
                        className="btn btn-success btn-sm d-flex align-items-center gap-1 p-2"
                        onClick={handleAddJob}
                    >
                        <i className="bi bi-plus-lg"></i>
                        + Post Event
                    </button>
                </div>

                {/* ACTION BAR */}
                <div className="bg-light border rounded-3 p-3 mb-4 d-flex flex-wrap align-items-center gap-3">
                    <div className="form-check ms-1">
                        <input
                            type="checkbox"
                            style={{ transform: "scale(0.8)", accentColor: "#0d6efd" }}
                            className="form-check-input"
                            id="selectAll"
                            checked={allSelected}
                            onChange={handleToggleSelectAll}
                        />
                        <label className="form-check-label ms-1" htmlFor="selectAll" style={{ color: "#333" }}>
                            Select All
                        </label>
                    </div>

                    <div className="flex-grow-1 d-flex gap-2">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search by Job Title or Location..."
                            value={searchInput}
                            onChange={(e) => {
                                setSearchInput(e.target.value);
                                setPage(1);
                            }}
                        />
                        <button className="btn btn-primary px-4" onClick={handleSearch}>
                            Search
                        </button>
                    </div>

                    <button className="btn btn-outline-danger" onClick={handleDelete}>
                        Delete ({selectedIds.length})
                    </button>
                </div>

                {/* JOB CARDS */}
                {currentJobs.map((job) => {
                    const counts = registrationCounts[job.id] || { total: 0, students: 0, employees: 0 };

                    return (
                        <div key={job.id} className="card mb-3 shadow-sm border-0">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div className="d-flex align-items-start gap-2">
                                        <input
                                            type="checkbox"
                                            style={{ transform: "scale(0.8)", accentColor: "#0d6efd" }}
                                            className="form-check-input mt-1"
                                            checked={selectedIds.includes(job.id)}
                                            onChange={() => handleToggleSelect(job.id)}
                                        />
                                        <div>
                                            <h6 className="mb-1">{job.title}</h6>
                                            <small className="text-muted">
                                                <TfiLocationPin /> {job.location}
                                            </small>
                                        </div>
                                    </div>

                                    <button
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={() => handlePreview(job)}
                                    >
                                        Event Preview
                                    </button>
                                </div>

                                {/* ✅ REGISTRATION COUNT BUTTONS */}
                                <div className="d-flex flex-wrap justify-content-center gap-3 my-2">

                                    {/* Total Applications */}
                                    <button
                                        className="btn btn-sm btn-dark d-flex flex-column align-items-center px-3"
                                        onClick={() => handleViewResponses(job)}
                                    >
                                        <span className="fw-bold fs-6">{counts.total}</span>
                                        <span className="small">Total Applications</span>
                                    </button>

                                    {/* Students */}
                                    <button
                                        className="btn btn-sm btn-outline-primary d-flex flex-column align-items-center px-3"
                                        onClick={() => handleViewResponses(job)}
                                    >
                                        <span className="fw-semibold">{counts.students}</span>
                                        <span className="small">Students</span>
                                    </button>

                                    {/* Employees / HR */}
                                    <button
                                        className="btn btn-sm btn-outline-success d-flex flex-column align-items-center px-3"
                                        onClick={() => handleViewResponses(job)}
                                    >
                                        <span className="fw-semibold">{counts.employees}</span>
                                        <span className="small">Employees</span>
                                    </button>

                                </div>

                                <div className="d-flex flex-column align-items-end mt-0 small">
                                    <span
                                        className={`badge ${job.status === "Open" ? "bg-success" : "bg-danger"}`}
                                        style={{ fontSize: "13px", marginBottom: "6px" }}
                                    >
                                        {job.status}
                                    </span>
                                    <span className="text-muted">
                                        Posted:{" "}
                                        <span className="text-warning fw-bold">{job.eventdate}</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}

                {/* Empty state */}
                {currentJobs.length === 0 && (
                    <div className="text-center py-5">
                        <h6 className="text-muted">No training programs found</h6>
                        <p className="text-muted small">Try adjusting your search filters</p>
                        <button className="btn btn-success mt-3" onClick={handleAddJob}>
                            <i className="bi bi-plus-circle me-2"></i>
                            Post Your First Event
                        </button>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <small className="text-muted">
                            Showing {(page - 1) * pageSize + 1} -{" "}
                            {Math.min(page * pageSize, filteredJobs.length)} of{" "}
                            {filteredJobs.length}
                        </small>
                        <div className="btn-group me-2 gap-2">
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                disabled={page === 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            <small className="text-muted">
                                Page {page} of {Math.max(1, totalPages)}
                            </small>
                            <button
                                className="btn btn-outline-secondary ms-2 btn-sm"
                                disabled={page === totalPages}
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

export default UstTotalApplications;